import {
  autoUpdate,
  offset,
  shift,
  useFloating,
  FloatingArrow,
  arrow,
  size,
} from "@floating-ui/react";
import clsx from "clsx";
import { FocusTrap } from "focus-trap-react";
import { observer } from "mobx-react";
import {
  FunctionComponent,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { CSSTransition } from "react-transition-group";

import BellIcon from "@ui/components/icon/BellIcon";
import LoginMessage from "@ui/components/notifications/LoginMessage/LoginMessage";
import NotificationsContent, {
  ContentMode,
} from "@ui/components/notifications/NotificationsContent/NotificationsContent";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import {
  NotificationsFilter,
  useNotificationsStore,
  useOptionalNotificationsDrawerContext,
} from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./NotificationsButtonBase.css";

interface NotificationsButtonBaseProps {
  popoverId?: string;
  popoverClassName?: string;
  type: "mobile" | "standard";
  Logo?: FunctionComponent<{ className?: string }>;
}

const NotificationsButtonBase = observer(function NotificationsButtonBase({
  popoverId,
  popoverClassName,
  type,
  Logo,
}: NotificationsButtonBaseProps) {
  const analytics = useAnalytics();
  const [allowNotificationsIconClick, setAllowNotificationsIconClick] =
    useState(true);
  const notificationsDrawer = useOptionalNotificationsDrawerContext();

  useEffect(() => {
    if (window.location.pathname === "/notifications") {
      setAllowNotificationsIconClick(false);
    }
  }, []);

  const hasLoadedInitialNotifications = useRef<boolean>(false);
  const prevIsNotificationsOpen = useRef<boolean>(false);

  const { user } = useAuth();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const { RootStore } = useNotificationsStore();

  const unseenCount =
    RootStore?.notificationsStore.notificationsStatus.unseen ?? 0;

  useEffect(() => {
    if (notificationsDrawer) {
      return;
    }
    if (RootStore?.userStore.viewerLoading || !RootStore?.userStore.viewer) {
      return;
    }

    // Only run when transitioning from closed to open
    const isOpening = !prevIsNotificationsOpen.current && isNotificationsOpen;
    prevIsNotificationsOpen.current = isNotificationsOpen;
    if (!isOpening) {
      return;
    }

    // If we haven't loaded any notifications yet, do the initial fetch
    if (!hasLoadedInitialNotifications.current) {
      void RootStore.allNotificationsStore.loadNotificationsForUser();
      void RootStore.unreadNotificationsStore.loadNotificationsForUser({
        filter: NotificationsFilter.Unread,
      });
      hasLoadedInitialNotifications.current = true;
    } else if (unseenCount > 0) {
      // otherwise if we have new unseen notifications, refresh the lists
      void RootStore.allNotificationsStore.refreshNotifications();
      void RootStore.unreadNotificationsStore.refreshNotifications({
        filter: NotificationsFilter.Unread,
      });
    }
  }, [RootStore, isNotificationsOpen, notificationsDrawer, unseenCount]);

  const handleNotificationsButtonClick = () => {
    // if current url is /notifications, do nothing because we already display
    // notifications on the page.
    if (!allowNotificationsIconClick) {
      return;
    }

    const areAnyNotificationsOpen =
      notificationsDrawer?.isDrawerOpen ?? isNotificationsOpen;

    if (!areAnyNotificationsOpen) {
      void RootStore?.userStore.setLastViewed(new Date());
      analytics?.emit({
        event: "notification_click",
      });
    }

    if (notificationsDrawer) {
      notificationsDrawer.toggleDrawer();
      return;
    }

    setIsNotificationsOpen((prev) => !prev);
  };

  const handleCloseNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
  }, []);

  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(10),
      shift({ padding: 30 }),
      arrow({ element: arrowRef }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight - 20, 600)}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  return (
    <>
      <button
        aria-label="Open Notifications"
        aria-expanded={notificationsDrawer?.isDrawerOpen ?? isNotificationsOpen}
        onClick={handleNotificationsButtonClick}
        ref={refs.setReference}
      >
        <span className={styles.notificationsIconContainer}>
          <BellIcon
            className={clsx(
              styles.notificationsIcon,
              !allowNotificationsIconClick && styles.notificationsIconDisabled,
            )}
            id="notifications-bell"
          />
          {RootStore?.notificationsStore.unseenNotificationsDisplayCount && (
            <div
              id="notifications-unseen-count"
              className={styles.notificationsBadge}
            >
              {RootStore.notificationsStore.unseenNotificationsDisplayCount}
            </div>
          )}
        </span>
      </button>

      {!notificationsDrawer && (
        <CSSTransition
          in={isNotificationsOpen}
          timeout={200}
          classNames={{
            enter: styles.enter[type],
            enterActive: styles.enterActive[type],
            exit: styles.exit[type],
            exitActive: styles.exitActive[type],
          }}
          nodeRef={refs.floating}
          unmountOnExit
        >
          <div
            className={
              type === "mobile" ? styles.mobileFocusTrapContainer : undefined
            }
          >
            {" "}
            <FocusTrap
              active={isNotificationsOpen}
              focusTrapOptions={{
                fallbackFocus: `#notifications-popover${popoverId ? `-${popoverId}` : ""}`,
                escapeDeactivates: false,
                clickOutsideDeactivates: (e: MouseEvent | TouchEvent) => {
                  if (e.target instanceof HTMLElement) {
                    const isBellIcon = e.target.id === "notifications-bell";
                    const isBadge = e.target.id === "notifications-badge";
                    const isUnseenCount =
                      e.target.id === "notifications-unseen-count";

                    if (!isBellIcon && !isBadge && !isUnseenCount) {
                      setIsNotificationsOpen(false);
                    }
                    return false;
                  }
                  return false;
                },
                allowOutsideClick: true,
                preventScroll: true,
              }}
            >
              <div
                id={`notifications-popover${popoverId ? `-${popoverId}` : ""}`}
                style={{
                  ...(type === "standard" ? floatingStyles : {}),
                }}
                ref={type === "standard" ? refs.setFloating : undefined}
                role="dialog"
                aria-modal="true"
                aria-label="Notifications"
                className={styles.popoverContainer[type]}
              >
                {type === "standard" && (
                  <FloatingArrow
                    ref={arrowRef}
                    context={context}
                    className={styles.popoverArrow}
                  />
                )}
                <div
                  className={clsx(
                    styles.popoverContent[type],
                    popoverClassName,
                  )}
                >
                  {user ? (
                    <>
                      {RootStore ? (
                        <NotificationsContent
                          showGoToNotificationsPageLink
                          onCloseNotifications={handleCloseNotifications}
                          mode={ContentMode.Modal}
                        />
                      ) : (
                        <div className={styles.notificationsSpinner} />
                      )}
                    </>
                  ) : (
                    <LoginMessage
                      Logo={Logo}
                      onCloseNotifications={handleCloseNotifications}
                    />
                  )}
                </div>
              </div>
            </FocusTrap>
          </div>
        </CSSTransition>
      )}
    </>
  );
});

export default NotificationsButtonBase;
